import { useSyncExternalStore } from 'react'
import { resolveSite, type SiteId } from './site'

/**
 * UI language for faces that ship more than one. Today only the Qatar face is
 * bilingual (English + Arabic); every other face is English-only and this
 * module always answers `en` there, so nothing they render can change.
 *
 * Resolution order on a bilingual face:
 *   1. `?lang=ar|en` — explicit, persisted so a demo link can pin a language.
 *   2. localStorage `sd_locale` — the visitor's last choice.
 *   3. Browser language — `ar-*` → Arabic, anything else → English. Qatar's
 *      banks default their sites to English with an Arabic toggle, so this
 *      matches what a customer there expects.
 *
 * `document.documentElement` gets `lang` + `dir` applied by the root layout
 * (see applyLocaleToDocument), which is what flips the whole page to RTL:
 * flex/grid mirror on their own, and the codemod to logical Tailwind classes
 * (ms-/me-/ps-/pe-/start-/end-) covers the physical ones.
 */
export type Locale = 'en' | 'ar'

const STORAGE_KEY = 'sd_locale'
const BILINGUAL_SITES = new Set<SiteId>(['qatar'])

export function siteIsBilingual(site: SiteId = resolveSite()): boolean {
  return BILINGUAL_SITES.has(site)
}

function isLocale(v: unknown): v is Locale {
  return v === 'en' || v === 'ar'
}

function readInitial(): Locale {
  if (typeof window === 'undefined') return 'en'
  if (!siteIsBilingual()) return 'en'
  const params = new URLSearchParams(window.location.search)
  const fromParam = params.get('lang')?.toLowerCase()
  if (isLocale(fromParam)) {
    try {
      window.localStorage.setItem(STORAGE_KEY, fromParam)
    } catch {
      /* ignore */
    }
    return fromParam
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (isLocale(stored)) return stored
  } catch {
    /* ignore */
  }
  const lang = (typeof navigator !== 'undefined' ? navigator.language : '') ?? ''
  return lang.toLowerCase().startsWith('ar') ? 'ar' : 'en'
}

let current: Locale | null = null
const listeners = new Set<() => void>()

export function getLocale(): Locale {
  if (current === null) current = readInitial()
  return current
}

export function setLocale(next: Locale) {
  if (!siteIsBilingual()) return
  if (next === getLocale()) return
  current = next
  try {
    window.localStorage.setItem(STORAGE_KEY, next)
  } catch {
    /* ignore */
  }
  applyLocaleToDocument(next)
  listeners.forEach(l => l())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, () => 'en' as Locale)
}

export function isRtl(locale: Locale = getLocale()): boolean {
  return locale === 'ar'
}

/** Sets `lang` and `dir` on <html>. Called by the root layout on mount and
 *  by setLocale on every switch, so RTL flips without a reload. */
export function applyLocaleToDocument(locale: Locale = getLocale()) {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  el.lang = locale
  el.dir = locale === 'ar' ? 'rtl' : 'ltr'
}

/**
 * Pick the localized member of a bilingual pair. `ar` is optional so a
 * partially translated record degrades to English rather than to a blank.
 */
export function pick<T>(en: T, ar: T | undefined, locale: Locale = getLocale()): T {
  return locale === 'ar' && ar !== undefined ? ar : en
}
